import { MAX_ERROR_CHARS, PROBE_TIMEOUT_MS } from './config';
import { timeoutSignal } from './timeout';
import type { ProbeOutcome } from './types';
import { ensureScheme, isParsableUrl, isPlainHttp } from './url';

/**
 * The `error` this module writes for a plain `http://` domain, and the one place
 * the literal is spelled.
 *
 * Exported because two screens have to recognise it: a mixed-content row is
 * `unhealthy` in the submitted data — the filter and the payload agree on that —
 * but it is not *dead*, and drawing it red would put a verdict on the screen
 * that the measurement never made. It used to be hand-copied into both of those
 * components with a note saying lifting it into `src/lib` would push a
 * presentation concern into the module that decides what gets submitted. It does
 * not: this string **is** what gets submitted, and it is this module that
 * decides to write it.
 */
export const MIXED_CONTENT_ERROR = 'mixed-content-blocked';

/**
 * Whether an outcome is Safari's mixed-content refusal rather than a
 * measurement — the question every consumer of the constant is really asking.
 *
 * Takes anything carrying an `error`, so a `CheckResult` fits without unwrapping.
 */
export function isMixedContentBlocked(outcome: Pick<ProbeOutcome, 'error'>): boolean {
  return outcome.error === MIXED_CONTENT_ERROR;
}

/**
 * Reachability probe — the whole check mechanism of this app (SPEC §7).
 *
 * ## Why this works at all
 *
 * `fetch(url, { mode: 'no-cors' })` returns an *opaque* response: no status, no
 * headers, no body. What it does keep is the promise's own behaviour, and that
 * follows exactly one rule — **it rejects only on a network-layer failure**
 * (DNS did not resolve, connection refused, TLS handshake failed, timeout). Any
 * real response at all, including 404, 403 and 500, makes it **resolve**. That
 * resolve/reject pair is the reachable / not-reachable signal.
 *
 * ## What it therefore cannot tell you
 *
 * `unhealthy` from here is trustworthy: nothing answered. `healthy` means only
 * *something answered* — an ISP that blocks by pointing DNS at a "this site is
 * blocked" page produces a genuine response and this probe calls it healthy,
 * while a real user opening the link sees the block page. Android catches that
 * with its browser round; there is no equivalent inside a WebClip and no way to
 * build one (SPEC §7.3, §14 #2). Anywhere this data is displayed, `healthy` has
 * to keep the meaning "probably alive", never "verified alive".
 *
 * ## Total
 *
 * Never throws — same invariant as Android's `LinkChecker.check`. Every failure
 * mode, including a synchronous `TypeError` from `fetch` and an unsupported
 * `AbortSignal`, comes back as an `unhealthy` outcome carrying a reason. A probe
 * that throws would abandon its whole round, and one bad URL in the backend list
 * would silently cost the run every link queued behind it.
 */
export async function probeReachable(
  domain: string,
  timeoutMs: number = PROBE_TIMEOUT_MS,
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<ProbeOutcome> {
  const url = ensureScheme(domain);

  // Checked BEFORE any fetch, and reported under its own name (SPEC §7.1).
  // Safari blocks every http:// request from an https:// page — no switch, no
  // exception. That is a fact about browser policy, constant and known in
  // advance; letting it land in the network `catch` below would blend it into
  // the "died because of the network" bucket, which is the one that varies and
  // the only one worth reading.
  if (isPlainHttp(url)) {
    return { healthStatus: 'unhealthy', responseMs: 0, error: MIXED_CONTENT_ERROR };
  }

  // A malformed URL makes `fetch` throw synchronously. Named separately so a
  // typo in the backend's link list cannot masquerade as a carrier block.
  if (!isParsableUrl(url)) {
    return { healthStatus: 'unhealthy', responseMs: 0, error: 'invalid-url' };
  }

  const started = now();
  const { signal, cancel } = timeoutSignal(timeoutMs);
  try {
    await fetchImpl(url, {
      method: 'GET',
      // The one mode that may talk to a domain that has never heard of us.
      // Swapping this for 'cors' would make every unconfigured domain fail at
      // the call site — see SPEC §5.
      mode: 'no-cors',
      // No cookies to third-party domains, ever. Same principle as the Android
      // app keeping its two OkHttp clients apart: the traffic that goes to
      // links being tested carries no credential of ours.
      credentials: 'omit',
      // Without this a repeat cycle can be answered from Safari's cache, and a
      // link that died an hour ago keeps reporting healthy from a stored copy.
      // The probe would then be measuring our own cache, not the network.
      cache: 'no-store',
      redirect: 'follow',
      signal,
    });
    // Deliberately does NOT read `response.status`. On an opaque response it is
    // always 0, and using it anywhere is a latent bug (SPEC §13 Never).
    return { healthStatus: 'healthy', responseMs: elapsed(started) };
  } catch (error) {
    return {
      healthStatus: 'unhealthy',
      responseMs: elapsed(started),
      error: describe(error),
    };
  } finally {
    cancel();
  }
}

/**
 * A short stable bucket, then the evidence: `"timeout (TimeoutError: …)"`.
 *
 * Split the way Android splits `FailureKind` from its cause chain, and for the
 * same reason: the bucket is what you group and count by across a fleet, the
 * detail is what you diagnose one device with. Capped at `MAX_ERROR_CHARS`
 * because a batch carries one of these per link.
 */
export function describe(error: unknown): string {
  const name = error instanceof Error ? error.name : typeof error;
  const message = error instanceof Error ? error.message.replace(/\s+/g, ' ').trim() : String(error);
  const kind = bucket(name, message);
  const detail = message === '' ? name : `${name}: ${message}`;
  const full = `${kind} (${detail})`;
  return full.length <= MAX_ERROR_CHARS ? full : `${full.slice(0, MAX_ERROR_CHARS - 1).trimEnd()}…`;
}

/**
 * The stable half of the error string.
 *
 * Kept coarse on purpose. A browser gives one opaque `TypeError: Load failed`
 * for DNS failure, connection refused and TLS rejection alike — inventing finer
 * buckets from message text would be guessing, and a guess that looks like a
 * classification is worse than an honest catch-all.
 */
function bucket(name: string, message: string): string {
  if (name === 'TimeoutError') return 'timeout';
  if (name === 'AbortError') return /timed? ?out/i.test(message) ? 'timeout' : 'aborted';
  return 'network-error';
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function elapsed(started: number): number {
  return Math.max(0, Math.round(now() - started));
}
